/*
This file builds CSS keyframes used for animations. It injects the CSS into the
DOM, and returns the corresponding CSS class name.
*/

type KeyframeStyles = {
  background?: string;
  color?: string;
  transform?: string;
  opacity?: number;
};

type KeyframeShape = Record<string, KeyframeStyles>;

const injectedKeyframes = new Set<string>();

function convertKeyframesToCSS(keyframes: KeyframeShape): string {
  return Object.entries(keyframes)
    .map(([percentage, keyframeProps]) => {
      const entries = Object.entries(keyframeProps);
      if (entries.length === 0) {
        // No styles defined for this keyframe, no need to define a keyframe for it
        return '';
      }

      const body = entries
        .map(([prop, value]) => `${prop}: ${value};`)
        .join(' ');
      return `${percentage} { ${body} }`;
    })
    .filter(Boolean)
    .join(' ');
}

function hashToClassname(str: string): string {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash * 16777619) >>> 0;
  }
  return hash.toString(36);
}

function getDeterministicAnimationName(keyframesCSS: string): string {
  return `kf_${hashToClassname(keyframesCSS)}`;
}

function injectKeyframeIntoDOM(name: string, css: string): void {
  if (typeof document === 'undefined' || injectedKeyframes.has(name)) return;

  const styleTag = document.createElement('style');
  styleTag.innerHTML = `@keyframes ${name} { ${css} }`;
  document.head.appendChild(styleTag);
  injectedKeyframes.add(name);
}

export function createSlideInDownAnimationString(
  init: KeyframeStyles,
  middle: KeyframeStyles,
  end: KeyframeStyles,
): string {
  const keyframes = {
    '0%': { ...init, transform: 'translateY(-100%)', opacity: 1 },
    '22%': { transform: 'translateY(0)' },
    '78%': middle,
    '100%': { ...end, opacity: 1 },
  };
  const css = convertKeyframesToCSS(keyframes);
  const cssClassName = getDeterministicAnimationName(css);
  injectKeyframeIntoDOM(cssClassName, css);
  return cssClassName;
}

export function createKeyframeAnimation(
  init: KeyframeStyles,
  middle: KeyframeStyles,
  end: KeyframeStyles,
): string {
  const keyframes = { '0%': init, '78%': middle, '100%': end };
  const css = convertKeyframesToCSS(keyframes);
  const cssClassName = getDeterministicAnimationName(css);
  injectKeyframeIntoDOM(cssClassName, css);
  return cssClassName;
}
